// Solomon Portal v2.x \u2014 /support route.
//
// Issue #9 lands the server-side conversation list + filter UI here. The
// previous BeaconEmbed-only view is preserved as a "Start a new
// conversation" CTA at the bottom of the page (still useful for new-thread
// creation; no longer the only thing on the page).
//
// State model:
//   - URL is the source of truth for filter state (status, q, range, page).
//     Pasting/sharing a URL reproduces the same view; refresh-safe.
//   - Search input is debounced 300ms; the *debounced* value goes into the
//     URL, the immediate value powers the controlled input.
//   - One in-flight request at a time; AbortController cancels superseded
//     fetches when filters change mid-flight.
//   - Audit events are fire-and-forget (writeAuditEvent never throws).
//
// Accessibility:
//   - The filter form is wrapped in <form role="search"> with named labels.
//   - Row count is announced via aria-live="polite".
//   - Status tab change focuses the table region (so SR users hear the new
//     count immediately).

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Search, MessageSquarePlus } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { useDebounce } from '../lib/hooks/use-debounce';
import { writeAuditEvent } from '../lib/audit';
// ADR-036 Day 6: Beacon retirement complete. The widget no longer
// loads in the SPA. The `beacon-signature` Edge Function stays
// deployed for 30 days as a deprecation window. At T+30 the function
// is removed and VITE_HELPSCOUT_BEACON_ID is dropped from
// .env.production via scripts/validate-prod-env.ts.
import {
  DEFAULT_PAGE_SIZE,
  listConversations,
  type ConversationListItem,
  type ConversationStatusFilter,
  type DateRangeFilter,
  type ListConversationsResult,
} from '../lib/conversation-list-api';
import { ConversationListTable } from '../components/support/ConversationListTable';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Pagination } from '../components/ui/pagination';
import { AuthErrorLayout } from '../components/auth/AuthErrorLayout';

const SECTION_LABEL_ID = 'support-list-region-title';
const STATUS_VALUES: ConversationStatusFilter[] = ['open', 'closed', 'all'];
const DATE_RANGE_VALUES: DateRangeFilter[] = ['30d', '90d', 'all'];
const SUPPORT_EMAIL = 'support@solomonadvising.com';

/**
 * ADR-037 backward-compat shim: prior to 2026-05-13 the OPEN tab used
 * value `'active'`. Bookmarks and old URLs may still contain it; map
 * them to the new `'open'` value so they don't fall through to the
 * default filter.
 */
const LEGACY_STATUS_ALIASES: Record<string, ConversationStatusFilter> = {
  active: 'open',
};

function isStatusFilter(v: string | null): v is ConversationStatusFilter {
  if (v === null) return false;
  const normalized = LEGACY_STATUS_ALIASES[v] ?? v;
  return (STATUS_VALUES as string[]).includes(normalized);
}

/**
 * Normalize an externally-supplied status value to the canonical
 * `ConversationStatusFilter`. Handles the ADR-037 'active' → 'open'
 * remap so legacy URLs don't fall through to the default.
 */
function normalizeStatusFilter(v: string | null): ConversationStatusFilter | null {
  if (v === null) return null;
  const candidate = LEGACY_STATUS_ALIASES[v] ?? v;
  return (STATUS_VALUES as string[]).includes(candidate)
    ? (candidate as ConversationStatusFilter)
    : null;
}
function isDateRangeFilter(v: string | null): v is DateRangeFilter {
  return v !== null && (DATE_RANGE_VALUES as string[]).includes(v);
}
function parsePage(v: string | null): number {
  if (v === null) return 1;
  const n = Number.parseInt(v, 10);
  if (!Number.isFinite(n) || n < 1) return 1;
  return n;
}

interface FilterState {
  status: ConversationStatusFilter;
  q: string;
  dateRange: DateRangeFilter;
  page: number;
}

type FetchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; items: ConversationListItem[]; total: number; hasMore: boolean }
  | { kind: 'error'; reason: ListConversationsResult['kind'] };

function pageFromState(state: FetchState, fallback: number): number {
  if (state.kind === 'ok' && state.total === 0) return 1;
  return fallback;
}

function totalPages(total: number, pageSize: number): number {
  if (total <= 0) return 1;
  return Math.max(1, Math.ceil(total / pageSize));
}

function StartConversationCta({
  onStart,
  configError,
}: {
  onStart: () => void;
  configError: string | null;
}): ReactNode {
  if (configError !== null) {
    return (
      <div role="alert" className="rounded-sm border border-border-med bg-white p-6 shadow-card">
        <h3 className="mb-2">Need to start a new conversation?</h3>
        <p className="text-text-body">{configError}</p>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3 rounded-sm border border-border-med bg-white p-6 shadow-card md:flex-row md:items-center md:justify-between">
      <div>
        <h3 className="mb-1">Need to start a new conversation?</h3>
        <p className="text-text-body">
          Open the support panel to send Solomon a new message. Replies route to your existing
          inbox.
        </p>
      </div>
      <Button type="button" onClick={onStart}>
        <MessageSquarePlus className="h-4 w-4" aria-hidden />
        <span>Start a conversation</span>
      </Button>
    </div>
  );
}

export function SupportRoute(): ReactNode {
  const navigateToNew = useNavigate();
  const handleStartConversation = (): void => {
    void navigateToNew('/support/new');
  };

  const { clientUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const filter: FilterState = useMemo<FilterState>(() => {
    const rawStatus = searchParams.get('status');
    const rawRange = searchParams.get('range');
    return {
      // ADR-037: normalize legacy 'active' → 'open' so bookmarked URLs
      // continue to land the user on the OPEN tab.
      status: normalizeStatusFilter(rawStatus) ?? 'open',
      q: searchParams.get('q') ?? '',
      dateRange: isDateRangeFilter(rawRange) ? rawRange : '90d',
      page: parsePage(searchParams.get('page')),
    };
  }, [searchParams]);

  // Local controlled state for the search input. The URL only updates after
  // 300ms of stability; the input itself updates synchronously for snappy
  // typing UX.
  const [searchInput, setSearchInput] = useState<string>(filter.q);
  const debouncedQ = useDebounce(searchInput, 300);

  // Sync debounced search back into the URL whenever it diverges from the
  // current URL value. We also reset page to 1 when the search changes.
  useEffect(() => {
    if (debouncedQ === filter.q) return;
    const next = new URLSearchParams(searchParams);
    if (debouncedQ.length > 0) next.set('q', debouncedQ);
    else next.delete('q');
    next.set('page', '1');
    setSearchParams(next, { replace: true });
    void writeAuditEvent({
      event_type: 'support.conversation_list.filter_applied',
      payload: {
        source: 'support-route.search',
        has_query: debouncedQ.length > 0,
        status: filter.status,
        date_range: filter.dateRange,
      },
    });
    // We deliberately do NOT depend on `filter.q` directly for the comparison
    // because that would cause a render loop after the URL settles.
  }, [debouncedQ, filter.q, filter.status, filter.dateRange, searchParams, setSearchParams]);

  // Filter setters \u2014 each resets page to 1 and updates the URL.
  const setStatus = useCallback(
    (status: ConversationStatusFilter) => {
      const next = new URLSearchParams(searchParams);
      next.set('status', status);
      next.set('page', '1');
      setSearchParams(next, { replace: true });
      void writeAuditEvent({
        event_type: 'support.conversation_list.filter_applied',
        payload: {
          source: 'support-route.status',
          status,
          date_range: filter.dateRange,
          has_query: filter.q.length > 0,
        },
      });
    },
    [searchParams, setSearchParams, filter.dateRange, filter.q],
  );

  const setDateRange = useCallback(
    (dateRange: DateRangeFilter) => {
      const next = new URLSearchParams(searchParams);
      next.set('range', dateRange);
      next.set('page', '1');
      setSearchParams(next, { replace: true });
      void writeAuditEvent({
        event_type: 'support.conversation_list.filter_applied',
        payload: {
          source: 'support-route.range',
          status: filter.status,
          date_range: dateRange,
          has_query: filter.q.length > 0,
        },
      });
    },
    [searchParams, setSearchParams, filter.status, filter.q],
  );

  const setPage = useCallback(
    (page: number) => {
      const next = new URLSearchParams(searchParams);
      next.set('page', String(page));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchInput('');
    const next = new URLSearchParams();
    next.set('status', 'open');
    next.set('range', '90d');
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  }, [setSearchParams]);

  // Fetching.
  const [fetchState, setFetchState] = useState<FetchState>({ kind: 'idle' });
  const filterKey = `${filter.status}|${filter.q}|${filter.dateRange}|${String(filter.page)}`;
  const filterKeyRef = useRef<string>('');

  useEffect(() => {
    if (filterKeyRef.current === filterKey) return;
    filterKeyRef.current = filterKey;
    const ctrl = new AbortController();
    setFetchState({ kind: 'loading' });
    void listConversations({
      status: filter.status,
      q: filter.q,
      dateRange: filter.dateRange,
      page: filter.page,
      pageSize: DEFAULT_PAGE_SIZE,
      signal: ctrl.signal,
    }).then((result) => {
      if (ctrl.signal.aborted) return;
      if (result.kind === 'ok') {
        setFetchState({
          kind: 'ok',
          items: result.response.items,
          total: result.response.total,
          hasMore: result.response.hasMore,
        });
        void writeAuditEvent({
          event_type: 'support.conversation_list.viewed',
          payload: {
            source: 'support-route.fetch',
            page: result.response.page,
            page_size: result.response.pageSize,
            status: filter.status,
            date_range: filter.dateRange,
            has_query: filter.q.length > 0,
            total: result.response.total,
          },
        });
      } else if (result.kind === 'aborted') {
        // Superseded fetch \u2014 do nothing.
      } else if (result.kind === 'unauthenticated') {
        // The Auth provider's onAuthStateChange will route to /login on
        // signOut; for an in-flight 401 we surface a recoverable error
        // rather than a silent redirect (the user can navigate manually).
        setFetchState({ kind: 'error', reason: 'unauthenticated' });
      } else {
        setFetchState({ kind: 'error', reason: result.kind });
      }
    });
    return () => ctrl.abort();
  }, [filterKey, filter.status, filter.q, filter.dateRange, filter.page]);

  // ADR-036 Day 6: Beacon init removed. The portal no longer loads
  // the Beacon widget; the new-conversation CTA navigates to
  // /support/new which renders the in-portal composer.
  const beaconConfigError: string | null = null;

  const handleSearchSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    // Form submit just flushes the debounced value immediately by re-using
    // the current input \u2014 the useEffect that syncs debouncedQ \u2192 URL will
    // pick it up on the next tick.
  };

  const items = fetchState.kind === 'ok' ? fetchState.items : [];
  const total = fetchState.kind === 'ok' ? fetchState.total : 0;
  const hasFilters = filter.q.length > 0 || filter.status !== 'open' || filter.dateRange !== '90d';

  // Error gate: if the fetch fails non-trivially, replace the table with a
  // recovery layout.
  if (fetchState.kind === 'error' && fetchState.reason !== 'aborted') {
    const reason = fetchState.reason;
    const title =
      reason === 'forbidden'
        ? 'Your portal access has been suspended'
        : "We couldn't load your conversations";
    const description =
      reason === 'forbidden'
        ? `Contact ${SUPPORT_EMAIL} to restore access.`
        : reason === 'upstream_unavailable'
          ? 'Our support backend is briefly unavailable. Please try again in a moment.'
          : 'Something went wrong loading the conversation list. Please try again.';
    return (
      <section aria-labelledby="support-title" className="max-w-3xl">
        <p className="mb-2 text-[10px] uppercase tracking-[0.2em] text-orange">Support</p>
        <h1 id="support-title" className="mb-4">
          How can we help, {clientUser?.full_name?.split(' ')[0] ?? 'friend'}?
        </h1>
        <AuthErrorLayout
          title={title}
          description={description}
          primary={{ label: 'Try again', onClick: () => window.location.reload() }}
          secondary={{ label: `Email ${SUPPORT_EMAIL}`, href: `mailto:${SUPPORT_EMAIL}` }}
        />
      </section>
    );
  }

  return (
    <section aria-labelledby="support-title" className="flex max-w-5xl flex-col gap-8">
      <header>
        <p className="mb-2 text-[10px] uppercase tracking-[0.2em] text-orange">Support</p>
        <h1 id="support-title" className="mb-4">
          How can we help, {clientUser?.full_name?.split(' ')[0] ?? 'friend'}?
        </h1>
        <p className="text-text-body">
          Search and filter your existing conversations with Solomon, or start a new one.
        </p>
      </header>

      <Tabs
        value={filter.status}
        onValueChange={(v) => {
          if (isStatusFilter(v)) setStatus(v);
        }}
      >
        <TabsList aria-label="Filter by conversation status">
          <TabsTrigger value="open">Open</TabsTrigger>
          <TabsTrigger value="closed">Closed</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      <form
        role="search"
        aria-label="Filter conversations"
        onSubmit={handleSearchSubmit}
        className="flex flex-col gap-3 md:flex-row md:items-end"
      >
        <div className="flex-1">
          <Label htmlFor="conversation-search" className="mb-1 block">
            Search conversations
          </Label>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted"
              aria-hidden
            />
            <Input
              id="conversation-search"
              type="search"
              autoComplete="off"
              placeholder="Search subject, sender, or message body\u2026"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
        <div className="md:w-48">
          <Label htmlFor="conversation-range" className="mb-1 block">
            Date range
          </Label>
          <Select
            value={filter.dateRange}
            onValueChange={(v) => {
              if (isDateRangeFilter(v)) setDateRange(v);
            }}
          >
            <SelectTrigger id="conversation-range" aria-label="Filter by date range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </form>

      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 id={SECTION_LABEL_ID} className="text-[18px] font-medium">
            Your conversations
          </h2>
          <p className="text-[13px] text-text-muted" aria-live="polite">
            {fetchState.kind === 'ok'
              ? total === 0
                ? 'No conversations match these filters.'
                : `${String(total)} conversation${total === 1 ? '' : 's'}`
              : fetchState.kind === 'loading'
                ? 'Loading\u2026'
                : ''}
          </p>
        </div>

        {fetchState.kind === 'ok' && items.length === 0 ? (
          hasFilters ? (
            <div
              role="status"
              className="flex flex-col items-start gap-3 rounded-sm border border-border-light bg-white p-8 text-center shadow-card md:items-center"
            >
              <p className="text-text-body">No conversations match your filters.</p>
              <Button type="button" variant="outline" onClick={clearFilters}>
                Clear filters
              </Button>
            </div>
          ) : (
            <div
              role="status"
              className="flex flex-col items-start gap-3 rounded-sm border border-border-light bg-white p-8 text-center shadow-card md:items-center"
            >
              <p className="text-text-body">You don&rsquo;t have any support conversations yet.</p>
              <Button type="button" onClick={handleStartConversation}>
                <MessageSquarePlus className="h-4 w-4" aria-hidden />
                <span>Start a conversation</span>
              </Button>
            </div>
          )
        ) : (
          <ConversationListTable
            items={items}
            loading={fetchState.kind === 'loading'}
            labelId={SECTION_LABEL_ID}
          />
        )}

        {fetchState.kind === 'ok' && total > DEFAULT_PAGE_SIZE ? (
          <Pagination
            page={pageFromState(fetchState, filter.page)}
            totalPages={totalPages(total, DEFAULT_PAGE_SIZE)}
            onPrev={() => setPage(Math.max(1, filter.page - 1))}
            onNext={() => setPage(filter.page + 1)}
            summary={`Showing ${String(items.length)} of ${String(total)}`}
          />
        ) : null}
      </div>

      <StartConversationCta onStart={handleStartConversation} configError={beaconConfigError} />
    </section>
  );
}
